import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wand2, Loader2, RefreshCw, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dinner-magic`;
const opts = { withCredentials: true, timeout: 90000 };
const DAY_SV = { mon: 'Måndag', tue: 'Tisdag', wed: 'Onsdag', thu: 'Torsdag', fri: 'Fredag', sat: 'Lördag', sun: 'Söndag' };

function Preview({ sugg, busyDay, onSwap, onApply, onClose }) {
  return (
    <div className="mt-3 rounded-xl border-2 border-[#D9822B] bg-white p-3 animate-in fade-in slide-in-from-top-2" data-testid="dinner-magic-preview">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-black text-[#8A4B1E]">✨ Farmors förslag, byt det du inte är sugen på</p>
        <button onClick={onClose} className="p-1 opacity-60 hover:opacity-100" aria-label="Stäng" data-testid="dinner-magic-close"><X className="w-4 h-4" /></button>
      </div>
      <ul className="space-y-1">
        {Object.keys(DAY_SV).map((k) => sugg[k] && (
          <li key={k} className="flex items-center gap-2 text-sm rounded-lg px-2 py-1.5 bg-[#FFF6EC]" data-testid={`dinner-magic-row-${k}`}>
            <span className="w-16 text-[11px] font-black uppercase text-[#B5672A]">{DAY_SV[k]}</span>
            <span className="flex-1 min-w-0" data-testid={`dinner-magic-dish-${k}`}>{sugg[k].dish}</span>
            {sugg[k].new && <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded-full bg-[#C8F2D8] text-[#1E6B3A]">ny idé</span>}
            <button onClick={() => onSwap(k)} disabled={!!busyDay} title="Byt den här"
              className="p-1 rounded-md text-[#B5672A] hover:bg-[#FCE3CC] disabled:opacity-40" data-testid={`dinner-magic-swap-${k}`}>
              {busyDay === k ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </button>
          </li>
        ))}
      </ul>
      <button onClick={onApply} disabled={!!busyDay}
        className="mt-3 w-full neo-btn py-2 bg-[#D9822B] text-white font-black flex items-center justify-center gap-2 disabled:opacity-50" data-testid="dinner-magic-apply">
        <Check className="w-4 h-4" /> Lägg in veckan
      </button>
    </div>
  );
}

export default function DinnerMagic({ monday, dinners, setDinners }) {
  const [busy, setBusy] = useState(null);
  const [sugg, setSugg] = useState(null);
  const filled = Object.values(dinners || {}).some((d) => (d || '').trim());

  const ask = async (direct) => {
    if (direct && filled && !window.confirm('Farmor byter ut hela veckans middagar. Okej?')) return;
    setBusy(direct ? 'direct' : 'preview');
    try {
      const { data } = await axios.post(`${API}/${monday}`, { current: dinners }, opts);
      if (direct) {
        apply(data.suggestions);
      } else setSugg(data.suggestions);
    } catch (e) { toast.error(e.response?.data?.detail || 'Elsa kom inte på något just nu'); }
    setBusy(null);
  };
  const apply = (s) => {
    setDinners(Object.fromEntries(Object.entries(s).map(([k, v]) => [k, v.dish])));
    setSugg(null);
    toast.success('Veckans middagar är fixade, gumman 💛');
  };
  const swap = async (day) => {
    setBusy(day);
    try {
      const current = Object.fromEntries(Object.entries(sugg).map(([k, v]) => [k, v.dish]));
      const { data } = await axios.post(`${API}/${monday}`, { day, current }, opts);
      setSugg((p) => ({ ...p, ...data.suggestions }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Kunde inte byta'); }
    setBusy(null);
  };

  return (
    <div data-testid="dinner-magic">
      <div className="mt-2 flex flex-wrap gap-2">
        <button onClick={() => ask(false)} disabled={!!busy}
          className="px-3 py-1.5 rounded-full text-xs font-black bg-gradient-to-r from-[#FFD6A5] to-[#FFC8DD] border-2 border-[#8A4B1E] text-[#8A4B1E] flex items-center gap-1.5 transition-transform hover:scale-105 disabled:opacity-60" data-testid="dinner-magic-btn">
          {busy === 'preview' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          {busy === 'preview' ? 'Farmor funderar…' : 'Middagsmagi'}
        </button>
        <button onClick={() => ask(true)} disabled={!!busy}
          className="px-3 py-1.5 rounded-full text-xs font-bold bg-white border border-[#F2C9A0] text-[#8A4B1E] flex items-center gap-1.5 hover:border-[#D9822B] disabled:opacity-60" data-testid="dinner-magic-direct">
          {busy === 'direct' && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Fyll i direkt
        </button>
      </div>
      {sugg && <Preview sugg={sugg} busyDay={typeof busy === 'string' && DAY_SV[busy] ? busy : null}
        onSwap={swap} onApply={() => apply(sugg)} onClose={() => setSugg(null)} />}
    </div>
  );
}
